import React, { useState } from 'react'
import { router, usePage, Link, Head } from '@inertiajs/react'
import { LockKeyhole, Loader2, Check, AlertCircle, Eye, EyeOff, ArrowLeft } from 'lucide-react'
import Marketing3AuthShell from '@/components/marketing3/Marketing3AuthShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { FALLBACK_BRAND } from '@/lib/brand'
import '../marketing/marketing3.css'
import { csrfToken } from '@/lib/csrf'

interface ResetPasswordProps {
  brand?: string | null
  resetPasswordToken: string
}

// Mirror of User::PASSWORD_MIN + the per-model complexity rule (app/models/user.rb). These are
// DISPLAY-ONLY hints; the backend Devise `:validatable` + strength validation is the source of truth.
const PASSWORD_MIN = 12

// mkt3 teal focus ring — see SignIn.tsx.
const FIELD_FOCUS = 'border-stone-200 focus-visible:ring-teal-600 focus-visible:ring-offset-0'

function passwordChecks(pw: string) {
  return [
    { label: `At least ${PASSWORD_MIN} characters`, ok: pw.length >= PASSWORD_MIN },
    { label: 'An uppercase letter', ok: /[A-Z]/.test(pw) },
    { label: 'A lowercase letter', ok: /[a-z]/.test(pw) },
    { label: 'A number', ok: /\d/.test(pw) },
  ]
}


export default function ResetPassword({ brand: brandProp, resetPasswordToken }: ResetPasswordProps) {
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND

  const { props } = usePage<{ flash?: { alert?: string | null; notice?: string | null } }>()
  const flashAlert = props.flash?.alert

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const checks = passwordChecks(password)
  const passwordValid = checks.every((c) => c.ok)
  const confirmMatches = confirm.length > 0 && confirm === password
  const canSubmit = passwordValid && confirmMatches && !submitting

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    // State-changing — router.put (the documented exception to the <Link> nav rule). Devise's
    // recoverable expects params nested under `user`, incl. the single-use reset token from the
    // email link. The controller redirects 303 on success (signs the user in) or back here on error.
    router.put(
      '/users/password',
      {
        user: {
          reset_password_token: resetPasswordToken,
          password,
          password_confirmation: confirm,
        },
      },
      {
        headers: { 'X-CSRF-Token': csrfToken() },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Marketing3AuthShell brand={brand}>
      <Head title="Choose a new password" />
      {/* Lead-in */}
      <div className="text-center">
        <h1 className="mkt3-display mkt3-h3">Choose a new password</h1>
        <p className="mkt3-prose mx-auto mt-2 text-sm">
          Pick a strong password. You&apos;ll be signed in right after.
        </p>
      </div>

      {flashAlert && (
        <div className="mt-6 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{flashAlert}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-7 space-y-5">
        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password" className="mkt3-label normal-case tracking-[0.12em] text-slate-500">
            New password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
              placeholder="Create a strong password"
              className={`h-11 rounded-lg bg-white pr-10 ${FIELD_FOCUS}`}
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {/* Live strength checklist — display only */}
          <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {checks.map((c) => (
              <li
                key={c.label}
                className={cn(
                  'flex items-center gap-1.5 text-xs transition-colors',
                  c.ok ? 'text-teal-700' : 'text-slate-500'
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded-full transition-colors',
                    c.ok ? 'bg-teal-100 text-teal-700' : 'bg-stone-100 text-slate-400'
                  )}
                >
                  <Check className="h-2.5 w-2.5" />
                </span>
                {c.label}
              </li>
            ))}
          </ul>
        </div>

        {/* Confirm */}
        <div className="space-y-2">
          <Label htmlFor="confirm" className="mkt3-label normal-case tracking-[0.12em] text-slate-500">
            Confirm new password
          </Label>
          <Input
            id="confirm"
            type={show ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            placeholder="Re-enter your password"
            className={`h-11 rounded-lg bg-white ${FIELD_FOCUS}`}
          />
          {confirm.length > 0 && !confirmMatches && (
            <p className="text-xs text-rose-600">Passwords don&apos;t match.</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="mkt3-cta h-11 w-full rounded-lg bg-teal-600 text-sm font-semibold text-white hover:bg-teal-700"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating…
            </>
          ) : (
            <>
              <LockKeyhole className="mr-2 h-4 w-4" />
              Set new password
            </>
          )}
        </Button>
      </form>

      {/* /users/sign_in IS an Inertia page, so <Link> is correct here. */}
      <p className="mt-7 text-center text-sm">
        <Link
          href="/users/sign_in"
          className="inline-flex items-center gap-1.5 font-medium text-teal-700 hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </Link>
      </p>
    </Marketing3AuthShell>
  )
}
